import * as signalR from '@microsoft/signalr';
import axios from 'axios';

const getApiBaseUrl = () => {
  // Use environment variable if set, otherwise default to relative path for deployed app
  return import.meta.env.VITE_SIGNALR_API_URL || '/api';
};

const negotiateSignalR = async userId => {
  const apiBaseUrl = getApiBaseUrl();
  const params = userId ? `?userId=${encodeURIComponent(userId)}` : '';
  const res = await axios.get(`${apiBaseUrl}/SignalRNegotiateFunc${params}`);
  return res.data;
};

class SignalRService {
  constructor() {
    this.connection = null;
    this.isConnected = false;
    this._userId = null;
  }

  async initialize(userId) {
    if (this.connection) {
      return this.connection;
    }

    this._userId = userId || null;
    const { url } = await negotiateSignalR(this._userId);

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: async () => {
          const { accessToken } = await negotiateSignalR(this._userId);
          return accessToken;
        },
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000]) // reconnect intervals in ms
      .build();

    return this.connection;
  }

  async start() {
    if (!this.connection) {
      throw new Error('Connection not initialized');
    }

    if (this.isConnected) {
      return;
    }

    await this.connection.start();
    this.isConnected = true;
  }

  async stop() {
    if (this.connection) {
      await this.connection.stop();
      this.isConnected = false;
      this.connection = null;
      this._userId = null;
      console.log('SignalR disconnected');
    }
  }

  on(method, callback) {
    if (this.connection) {
      this.connection.on(method, callback);
    }
  }

  off(method, callback) {
    if (this.connection) {
      this.connection.off(method, callback);
    }
  }

  async invoke(method, ...args) {
    if (this.connection && this.isConnected) {
      return this.connection.invoke(method, ...args);
    }
    throw new Error('SignalR connection not established');
  }

  getConnectionState() {
    return {
      isConnected: this.isConnected,
      connectionId: this.connection?.connectionId || null
    };
  }

  onReconnecting(callback) {
    if (this.connection) {
      this.connection.onreconnecting(callback);
    }
  }
  onReconnected(callback) {
    if (this.connection) {
      this.connection.onreconnected(callback);
    }
  }
  onClose(callback) {
    if (this.connection) {
      this.connection.onclose(callback);
    }
  }
}

const signalRService = new SignalRService();
export default signalRService;
